export function cx(...parts) {
    return parts
        .filter((part) => typeof part === 'string' && part.length > 0)
        .join(' ');
}
/**
 * Two-character avatar fallback that works for latin and CJK names.
 * "Alex Rivera" -> "AR", "alex" -> "AL", "张伟" -> "张伟", "" -> "?".
 */
export function initialsFromName(name) {
    const trimmed = name.trim();
    if (!trimmed)
        return '?';
    const tokens = trimmed.split(/[\s._\-@]+/).filter((token) => token.length > 0);
    const first = tokens[0];
    if (!first)
        return '?';
    if (tokens.length >= 2) {
        const last = tokens[tokens.length - 1];
        const firstChars = Array.from(first);
        const lastChars = last ? Array.from(last) : firstChars;
        return `${firstChars[0] ?? '?'}${lastChars[0] ?? ''}`.toUpperCase();
    }
    const chars = Array.from(first);
    if (chars.length === 1)
        return chars[0]?.toUpperCase() ?? '?';
    // CJK names are usually one token; two glyphs read better than one glyph + nothing.
    const cjk = /[\u3400-\u9fff\uf900-\ufaff]/.test(first);
    return (cjk ? chars.slice(0, 2).join('') : chars.slice(0, 2).join('')).toUpperCase();
}
/** Deterministic avatar hue in [0, 359] derived from the display name. */
export function avatarHue(name) {
    let hash = 0;
    const text = name.trim() || 'guest';
    for (let index = 0; index < text.length; index += 1) {
        hash = (hash * 31 + text.charCodeAt(index)) % 360;
    }
    return hash;
}
export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
/** Inline CSS custom properties without `as any`; used for avatar hue and grid columns. */
export function cssVars(vars) {
    return vars;
}
const QUALITY_KEYS = {
    0: 'network.unknown',
    1: 'network.excellent',
    2: 'network.good',
    3: 'network.poor',
    4: 'network.bad',
    5: 'network.veryBad',
    6: 'network.disconnected',
};
/** i18n key for a network-quality value; components resolve it through `t()`. */
export function qualityLabelKey(value) {
    if (value === null || value === undefined)
        return QUALITY_KEYS[0];
    return QUALITY_KEYS[value] ?? QUALITY_KEYS[0];
}
/** English fallback kept for non-React hosts and backwards compatibility. */
export function qualityLabel(value) {
    const labels = {
        'network.unknown': 'Unknown',
        'network.excellent': 'Excellent',
        'network.good': 'Good',
        'network.poor': 'Poor',
        'network.bad': 'Bad',
        'network.veryBad': 'Very bad',
        'network.disconnected': 'Disconnected',
    };
    return labels[qualityLabelKey(value)];
}
export function qualityTone(value) {
    if (value === null || value === undefined || value === 0)
        return 'unknown';
    if (value <= 2)
        return 'good';
    if (value === 3)
        return 'fair';
    if (value <= 5)
        return 'poor';
    return 'offline';
}
/** 0-255 Agora volume -> 0-100 integer for the ARIA meter. */
export function volumePercent(level) {
    if (!Number.isFinite(level))
        return 0;
    return Math.round((clamp(level, 0, 255) / 255) * 100);
}
export function formatClockTime(epochMs) {
    if (!Number.isFinite(epochMs))
        return '--:--';
    const date = new Date(epochMs);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}
export function isBrowserEnvironment() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
export function errorMessage(error, fallback = '') {
    if (error instanceof Error)
        return error.message;
    if (typeof error === 'string')
        return error;
    return fallback;
}
//# sourceMappingURL=utils.js.map